import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import * as assert from "typed-assert";
import debug from "debug";
import { v4 as uuidv4 } from "uuid";

import { FormConfig } from "@evergiving/common/dist/es2015/form";
import {
  Amount,
  FormSubmissionV1,
  DonationIntent,
  Donor,
  PaymentFrequency,
  PaymentSource,
  DeferredSubmissionResponse,
  ServerSubmissionResponse,
  FinalSubmissionResponse,
  SubmissionResult,
  SubmissionState,
  DonorPayload,
  PaymentSourcePayload,
  DonorWithDonationPayload,
  FormSubmissionPayload,
  StripeErrorType,
  StripeErrorCode,
} from "@evergiving/common/dist/es2015/pledge";
import { wait } from "@evergiving/common/dist/es2015/utils";
import { RootState } from "@/lib/redux/types";
import { amountSelector } from "@/lib/misc/amountSelector";
import { StripePaymentContext } from "@/lib/hooks/usePaymentCommon";

const log = debug(__filename);

const sessionUuid = uuidv4();

const clientBuildId = process.env.NEXT_BUILD_ID;
const formVersionToken = process.env.DIGITAL_FORM_VERSION_TOKEN;
const environment = process.env.EVERGIVING_ENVIRONMENT;

enum SubmissionClientErrorReason {
  NetworkError,
  InvalidRequest,
  InvalidServerResponse,
  InvalidServerResponseBody,
  StripeContextUnavailable,
  StripeActionUnavailable,
  StripeError,
}

interface SubmissionClientError {
  result: "clientError";
  reason: SubmissionClientErrorReason;
  detail?: Error | number;
}

interface SubmissionTimeoutError {
  result: "timeoutError";
}

function assertServerSubmissionResponse(
  input: unknown
): asserts input is ServerSubmissionResponse {
  // FIXME
}

function serializeDonationIntent(
  amount: Amount,
  frequency: PaymentFrequency,
  startDate: string | undefined,
  paymentDay: number | undefined,
  giftAidConsent: true | false | "non-taxpayer"
) {
  const donationIntent: DonationIntent = {
    amount,
    frequency,
    ...(startDate === undefined ? {} : { startDate }),
    ...(giftAidConsent === undefined ? {} : { giftAidConsent }),
    paymentDay,
  };
  return Buffer.from(JSON.stringify(donationIntent), "utf-8").toString(
    "base64"
  );
}

async function fetchDeferredResponse({
  ablyClientKey,
  ablyChannel,
}: {
  ablyClientKey: string;
  ablyChannel: string;
}): Promise<DeferredSubmissionResponse | SubmissionTimeoutError> {
  const Ably = await import("ably/promises");
  const client = new Ably.Realtime({
    key: ablyClientKey,
  });
  try {
    const channel = client.channels.get(ablyChannel, {
      params: { rewind: String(1) },
      cipher: undefined,
      modes: ["SUBSCRIBE"],
    });
    const responsePromise = new Promise<DeferredSubmissionResponse>(
      (resolve, reject) => {
        channel.subscribe((message) => {
          try {
            const payload = JSON.parse(message.data);
            log("Received message from Ably: ", payload);

            // Attach ably details to be used in 3DS dance
            if (payload.result === SubmissionResult.StripeRequiresAction) {
              payload.ablyClientKey = ablyClientKey;
            }

            // FIXME: validate JSON schema
            resolve(payload as DeferredSubmissionResponse);
          } catch (e) {
            reject(e);
          }
        });
      }
    );
    const timeoutPromise = wait<SubmissionTimeoutError>(12_000, {
      result: "timeoutError",
    });
    return await Promise.race([responsePromise, timeoutPromise]);
  } finally {
    client.close();
  }
}

async function fetchSubmissionResponse(
  submissionEndpointUrl: string,
  body: FormData
): Promise<
  DeferredSubmissionResponse | SubmissionClientError | SubmissionTimeoutError
> {
  let response;
  try {
    response = await fetch(submissionEndpointUrl, {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      credentials: "omit",
      body,
    });
  } catch (e) {
    return {
      result: "clientError",
      reason: SubmissionClientErrorReason.InvalidRequest,
      detail: Error(String(e)),
    };
  }

  if (!response.ok && response.status !== 400) {
    return {
      result: "clientError",
      reason: SubmissionClientErrorReason.InvalidServerResponse,
      detail: response.status,
    };
  }

  let responseBody;
  try {
    responseBody = await response.json();
  } catch (e) {
    return {
      result: "clientError",
      reason: SubmissionClientErrorReason.InvalidServerResponseBody,
      detail: Error(String(e)),
    };
  }

  try {
    assertServerSubmissionResponse(responseBody);
  } catch (e) {
    return {
      result: "clientError",
      reason: SubmissionClientErrorReason.InvalidServerResponseBody,
      detail: Error(String(e)),
    };
  }

  if (responseBody.result === SubmissionResult.Deferred) {
    log("Deferred result!", responseBody);
    return fetchDeferredResponse(responseBody);
  }

  return responseBody;
}

interface SubmissionPayload {
  donor?: Donor;
  paymentSource?: PaymentSource;
  submissionState?: SubmissionState;
  stripePaymentContext?: StripePaymentContext;
}

export type FormSubmitFunction = (
  submissionPayload: SubmissionPayload
) => Promise<
  FinalSubmissionResponse | SubmissionClientError | SubmissionTimeoutError
>;

export function useFormSubmission(formConfig: FormConfig): {
  submit: FormSubmitFunction;
  donationIntentBase64?: string;
} {
  const previousSubmissionState = useSelector(
    (state: RootState) => state.previousSubmissionState
  );
  const testingReference = useSelector(
    (state: RootState) => state.testingReference
  );
  const formSourceRef = useSelector((state: RootState) => state.formSourceRef);
  const pledgePaymentAuthorization = useSelector(
    (state: RootState) => state.formPledgePaymentAuthorization
  );
  log("useFormSubmission, formSourceRef=", formSourceRef);

  const amount = useSelector(amountSelector);
  const frequency = useSelector((state: RootState) => state.paymentFrequency);
  const giftAidConsent = useSelector(
    (state: RootState) => state.giftAidConsent
  );
  const startDate = useSelector((state: RootState) => state.startDate);
  const paymentDay = useSelector((state: RootState) => state.paymentDay);

  const [donationIntentBase64, setDonationIntentBase64] = useState<string>(
    "payment" in formConfig && frequency !== undefined
      ? serializeDonationIntent(
          amount,
          frequency,
          startDate,
          paymentDay,
          giftAidConsent
        )
      : ""
  );

  useEffect(() => {
    if ("payment" in formConfig) {
      assert.isNotUndefined(frequency);
      setDonationIntentBase64(
        serializeDonationIntent(
          amount,
          frequency,
          startDate,
          paymentDay,
          giftAidConsent
        )
      );
    }
  }, [amount, frequency, startDate, giftAidConsent, paymentDay, formConfig]);

  const { campaignUuid } = formConfig;

  const submit = async (
    submissionPayload: SubmissionPayload
  ): Promise<
    FinalSubmissionResponse | SubmissionClientError | SubmissionTimeoutError
  > => {
    const { donor, paymentSource, submissionState, stripePaymentContext } =
      submissionPayload;

    const submitDonor = donor !== undefined;
    const submitPaymentSource = paymentSource !== undefined;
    const submitDonation = donationIntentBase64 !== "";

    let payload: FormSubmissionPayload;
    if (submitDonor && submitDonation && submitPaymentSource) {
      payload = {
        donor,
        donationIntentBase64,
        paymentSource,
      } as DonorWithDonationPayload;
    } else if (submitDonor) {
      payload = { donor } as DonorPayload;
    } else {
      payload = { paymentSource } as PaymentSourcePayload;
    }

    assert.isNotUndefined(clientBuildId);
    assert.isNotUndefined(environment);

    const formSubmission: FormSubmissionV1 = {
      version: 1,
      metadata: {
        campaignUuid,
        sessionUuid,
        testingReference,
        formVersionToken,
        clientBuildId,
        environment,
        formSourceRef,
      },
      previousSubmissionState: submissionState ?? previousSubmissionState,
      ...(pledgePaymentAuthorization
        ? {
            pledgePaymentAuthorizationToken: pledgePaymentAuthorization.token,
          }
        : {}),
      payload,
    };

    const body = new FormData();
    body.append(
      "blob",
      new Blob([JSON.stringify(formSubmission)], {
        type: "application/json",
      })
    );

    const submissionResponse = await fetchSubmissionResponse(
      formConfig.submissionEndpointUrl,
      body
    );

    // Attach payment method from Stripe Payment Elements and attempt to confirm payment
    if (
      submissionResponse.result === SubmissionResult.StripeRequiresPaymentMethod
    ) {
      if (!stripePaymentContext) {
        return {
          result: "clientError",
          reason: SubmissionClientErrorReason.StripeContextUnavailable,
        };
      }

      const { paymentElements, stripeInstance } = stripePaymentContext;

      const paymentResult = await stripeInstance.confirmPayment({
        ...(paymentElements ? { elements: paymentElements } : {}),
        clientSecret: submissionResponse.clientSecret,
        redirect: "if_required",
        confirmParams: {
          return_url: window.location.href, // FIXME
        },
      });

      const { error, paymentIntent } = paymentResult;
      if (error) {
        return {
          result: SubmissionResult.StripeError,
          state: submissionResponse.state,
          type: error.type as StripeErrorType,
          code: error.code as StripeErrorCode,
          declineCode: error.decline_code,
          message: error.message,
          param: error.param,
          charge: error.charge,
        };
      }

      // By Stripe Documentation, if there is no error, confirmation was successful.
      // Check anyways...¯\_(ツ)_/¯
      if (paymentIntent?.status === "succeeded") {
        return submit({
          donor,
          paymentSource,
          submissionState: {
            ...submissionResponse.state,
            paymentIntentId: paymentIntent.id,
          },
        });
      }

      // We should never reach this point.
      return {
        result: "clientError",
        reason: SubmissionClientErrorReason.StripeActionUnavailable, // FIXME
      };
    }

    return submissionResponse;
  };

  return donationIntentBase64 !== ""
    ? { submit, donationIntentBase64 }
    : { submit };
}
